import { FC, ReactElement, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useAuthRedirect from 'hooks/useAuthRedirect';
import ContextError from 'components/ContextError';
import {
  Container,
  Box,
  Grid,
  Skeleton,
  useMediaQuery,
  useTheme,
  Fab,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import VideoSubject from 'components/VideoSubject';
import VideoSubjectSkeleton from 'components/VideoSubjectSkeleton';
import { usePlayback } from 'contexts/WatchContext';
import { useClassContextQuery } from 'hooks/apiHooks';
import { ownerPath, videoPath, classPath } from 'routes';
import { Link } from 'react-router-dom';
import { getTitleFromCode } from 'utils/ClassUtils';
import type { VideoSubjectMetadataMessage } from '@/types/VideoSubjectMetadata';
import ClassHeader from 'components/ClassHeader';
import OverflowFilterBar from 'components/OverflowFilterBar';
import { NoSnippet } from 'utils/NoSnippet';

/** Route params for ClassContext: matches '/t/:titleClass/:titleLingo' **/
interface ClassContextParams {
  /** allow index signature for useParams mapping */
  [key: string]: string | undefined;
  titleClass?: string;
  titleLingo?: string;
}

/**
 * Page displaying a class-based catalog of videos.
 */
const ClassContext: FC = (): ReactElement | null => {
  const theme = useTheme();
  const { t } = useTranslation('titleToolbar');
  const isXSmall = useMediaQuery(theme.breakpoints.only('xs'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Extract 'titleClass' and 'titleLingo' from route params
  const { titleClass = '', titleLingo = 'en' } = useParams<ClassContextParams>();
  // Scroll to top on class or lingo change
  useEffect(() => { window.scrollTo(0, 0); }, [titleClass, titleLingo]);

  // Playback context for queue and control
  const { play, isQueueMatch, currentVideo } = usePlayback();

  // Local filter state: extra class filter
  const [extraClass, setExtraClass] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<{ titleClass: string; titleLingo: string } | null>(null);
  // Clear filter when route parameters change (fresh explore)
  useEffect(() => {
    setExtraClass('');
    setSelectedFilter(null);
  }, [titleClass, titleLingo]);
  // Fetch catalog data with optional extraClass filter
  const { data, error, isLoading } = useClassContextQuery(titleLingo, titleClass, extraClass);
  useAuthRedirect(error);

  const videoBlock: VideoSubjectMetadataMessage[] = data?.videoMetadata?.videoMetadata ?? [];
  const header = getTitleFromCode(data?.classCategory?.titleClass ?? '');
  // Set document title to class header
  useEffect(() => {
    document.title = `${t('title')} - ${header}`;
  }, [header, t]);
  const queueMatch = isQueueMatch(videoBlock);
  // Disable FAB only when queue matches and a video is currently playing
  const disableFab = queueMatch && Boolean(currentVideo);
  // Class filter options
  const classBlock = useMemo(
    () => data?.classMetadata?.classMetadata ?? [],
    [data]
  );

  // Loading state: skeletons
  if (isLoading) {
    const skeletonCount = 12;
    return (
      <Container maxWidth="xl" disableGutters={isXSmall}>
      <Box sx={{ px: { sm: 2 }, mb: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={theme.spacing(17)} />
        </Box>
        <Grid container sx={{ mx: { xs: -1, sm: 0 } }}>
          {Array.from({ length: skeletonCount }).map((_, idx) => (
            <Grid
              key={idx}
              size={{ xs: 6, md: 4, xl: 3 }}
              sx={{ px: { xs: 1, sm: 2 }, py: { xs: 2 } }}
            >
              <VideoSubjectSkeleton />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Not-found state
  if (data === null) {
    return <ContextError message={t('notFound')} />;
  }
  // Error state
  if (error) {
    const status = (error as any)?.response?.status;
    const message = status === 404 ? t('notFound') : t('errorLoading');
    return <ContextError message={message} />;
  }

  // (we now use Link props instead of click handlers)

  return (
    <Container maxWidth="xl" disableGutters={isXSmall}>
      {/* Class header with horizontal padding aligned to grid */}
      <Box sx={{ px: { sm: 2 }, mb: 2 }}>
        <ClassHeader title={header} lingo={titleLingo} />
      </Box>
      {/* Class filters strip */}
      <NoSnippet>
      {classBlock.length > 0 && (
        <OverflowFilterBar
          filters={classBlock}
          selectedFilter={selectedFilter}
          onFilterSelect={(c) => {
            setSelectedFilter(c);
            setExtraClass(c ? c.titleClass : '');
          }}
        />
      )}
      </NoSnippet>
      {/* Video grid */}
      <Grid container sx={{ mx: { xs: -1, sm: 0 } }}>
        {videoBlock.map((video) => (
          <Grid
            key={video.musicVideo}
            size={{ xs: 6, md: 4, xl: 3 }}
            sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
          >
          <VideoSubject
            video={video}
            videoLink={{
              to: videoPath(video.musicVideo),
              state: { facadeQueue: videoBlock, shadowQueue: videoBlock },
            }}
            ownerLink={{
              to: ownerPath(video.musicOwner)
            }}
          />
          </Grid>
        ))}
      </Grid>
      {/* Navigate to full title explore for selected class filter */}
      {selectedFilter && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <IconButton
            component={Link}
            to={classPath(titleLingo, selectedFilter.titleClass)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
      {/* Queue or shuffle controls */}
      {videoBlock.length > 0 && (
        <Fab
          color="primary"
          disabled={disableFab}
          onClick={() => {
            play(videoBlock[0], videoBlock);
          }}
          sx={{
            position: 'fixed',
            bottom: isMobile ? theme.spacing(9) : theme.spacing(2),
            right: theme.spacing(2),
          }}
        >
          <PlayArrowIcon />
        </Fab>
      )}
    </Container>
  );
};

export default ClassContext;